import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function Analytics() {
  const [period, setPeriod] = useState("week");
  const [shift, setShift] = useState("all");
  const [trend, setTrend] = useState([]);
  const [skuPerf, setSkuPerf] = useState([]);

  useEffect(() => {
    (async () => {
      const { data: t } = await api.get(`/analytics/production-trend?period=${period}&shift=${shift}`);
      setTrend(t);
    })();
  }, [period, shift]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/analytics/sku-performance?shift=${shift}`);
      setSkuPerf(data);
    })();
  }, [shift]);

  const totalProduced = trend.reduce((a, r) => a + r.output_kg, 0);
  const totalRejected = trend.reduce((a, r) => a + r.rejected, 0);
  const avgYield = trend.length ? (trend.reduce((a, r) => a + r.avg_yield, 0) / trend.length).toFixed(1) : 0;

  const topSkus = skuPerf.slice(0, 8);
  const slowSkus = skuPerf.filter(s => s.packed > 0).slice(-5).reverse();

  return (
    <div data-testid="analytics-page">
      <PageHeader subtitle="Production Analytics" title="Trends & Insights"
        actions={
          <div className="flex flex-wrap gap-2">
            <Tabs value={shift} onValueChange={setShift}>
              <TabsList>
                <TabsTrigger value="all" data-testid="shift-all">All</TabsTrigger>
                <TabsTrigger value="day" data-testid="shift-day">Day 6–18</TabsTrigger>
                <TabsTrigger value="night" data-testid="shift-night">Night 18–6</TabsTrigger>
              </TabsList>
            </Tabs>
            <Tabs value={period} onValueChange={setPeriod}>
              <TabsList>
                <TabsTrigger value="day" data-testid="period-day">Day</TabsTrigger>
                <TabsTrigger value="week" data-testid="period-week">Week</TabsTrigger>
                <TabsTrigger value="month" data-testid="period-month">Month</TabsTrigger>
                <TabsTrigger value="year" data-testid="period-year">Year</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
        <Card className="shadow-none"><CardContent className="p-5"><div className="overline mb-2">Total Produced</div><div className="text-metric text-3xl text-primary">{totalProduced.toFixed(1)}<span className="text-base">kg</span></div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-5"><div className="overline mb-2">Avg Yield</div><div className="text-metric text-3xl text-emerald-600">{avgYield}%</div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-5"><div className="overline mb-2">Rejected Qty</div><div className="text-metric text-3xl text-red-600">{totalRejected}</div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="overline mb-4">Production Output (kg)</div>
            <div className="h-64">
              <ResponsiveContainer>
                <AreaChart data={trend}>
                  <defs><linearGradient id="ga1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4}/><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                  <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))"/>
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))"/>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }}/>
                  <Area type="monotone" dataKey="output_kg" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#ga1)"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="overline mb-4">Yield Trend</div>
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                  <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))"/>
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))"/>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }}/>
                  <Line type="monotone" dataKey="avg_yield" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={{ fill: "hsl(var(--chart-2))" }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="overline mb-4">Wastage / Rejected (Daily)</div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                  <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))"/>
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))"/>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }}/>
                  <Bar dataKey="rejected" fill="hsl(var(--destructive))" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="overline mb-4">Top SKUs (Packed Qty)</div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={topSkus} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                  <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))"/>
                  <YAxis type="category" dataKey="sku_code" fontSize={10} width={70} stroke="hsl(var(--muted-foreground))"/>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }}/>
                  <Bar dataKey="packed" fill="hsl(var(--primary))" radius={[0,3,3,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="overline mb-3">Most Produced / Sold</div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Packed</TableHead>
                <TableHead className="text-right">Sold</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {topSkus.map(s => (
                  <TableRow key={s.sku_id}>
                    <TableCell><div className="text-sm">{s.sku_name}</div><div className="mono text-xs text-muted-foreground">{s.sku_code}</div></TableCell>
                    <TableCell className="text-right mono">{s.packed}</TableCell>
                    <TableCell className="text-right mono">{s.sold}</TableCell>
                    <TableCell className="text-right mono text-primary">₹{s.revenue.toLocaleString("en-IN")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="overline mb-3">Slow Moving SKUs</div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Packed</TableHead>
                <TableHead className="text-right">Sold</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {slowSkus.map(s => (
                  <TableRow key={s.sku_id}>
                    <TableCell><div className="text-sm">{s.sku_name}</div><div className="mono text-xs text-muted-foreground">{s.sku_code}</div></TableCell>
                    <TableCell className="text-right mono">{s.packed}</TableCell>
                    <TableCell className="text-right mono">{s.sold}</TableCell>
                    <TableCell>
                      {s.sold === 0
                        ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Dead Stock</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Slow</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
                {slowSkus.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No slow-moving SKUs</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
